import { useEffect, useState } from "react";
import { FiX, FiDownload, FiPrinter } from "react-icons/fi";

const OrderPaymentReceiptsDrawer = ({ isOpen, onClose, receiptData }) => {
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  useEffect(() => {
    if (receiptData?.receipts?.length > 0) {
      setSelectedReceipt(receiptData.receipts[0]);
    }
  }, [receiptData]);

  if (!isOpen || !receiptData) return null;

  // DOWNLOAD PDF
  const handleDownload = async (url, filename = "receipt.pdf") => {
    try {
      if (!url) return;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch PDF");
      }

      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = blobUrl;

      link.setAttribute("download", filename);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);

      // fallback
      window.open(url, "_blank");
    }
  };

  // PRINT PDF
  const handlePrint = () => {
    if (!selectedReceipt?.receipt_url) return;

    const printWindow = window.open(selectedReceipt.receipt_url, "_blank");

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();

        printWindow.print();
      };
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      {/* MODAL */}
      <div className="w-full max-w-7xl h-[90vh] bg-[#F8FAFC] rounded-3xl overflow-hidden shadow-2xl border border-gray-200 flex flex-col">
        {/* HEADER */}
        <div className="bg-white border-b border-gray-200 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900">
              Payment Receipts
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              #{receiptData?.orderNumber}
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-all"
          >
            <FiX className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT PANEL */}
          <div className="w-[280px] bg-white border-r border-gray-200 flex flex-col">
            {/* RECEIPT LIST */}
            <div className="flex-1 overflow-y-auto">
              {/* LIST HEADER */}
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Receipt List</h3>
              </div>

              {/* LIST */}
              <div className="p-2">
                {receiptData?.receipts?.map((receipt, index) => {
                  const isActive = selectedReceipt?._id === receipt?._id;

                  return (
                    <button
                      key={receipt?._id}
                      onClick={() => setSelectedReceipt(receipt)}
                      className={`w-full text-left px-4 py-4 border-b transition-all rounded-xl ${
                        isActive
                          ? "bg-blue-50 border-blue-100"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                    >
                      {/* RECEIPT ID */}
                      <div className="font-semibold text-gray-900">
                        Receipt-
                        {String(receiptData?.receipts?.length - index).padStart(
                          3,
                          "0",
                        )}
                      </div>

                      {/* AMOUNT + METHOD */}
                      <div className="text-sm font-medium text-gray-700 mt-2">
                        ₹
                        {Number(receipt?.paid_amount || 0)
                          .toFixed(0)
                          .toLocaleString("en-IN")}
                        {" • "}
                        <span className="uppercase">
                          {receipt?.payment_mode || "cash"}
                        </span>
                      </div>

                      {/* DATE */}
                      <div className="text-xs text-gray-400 mt-2">
                        {new Date(receipt?.payment_date).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="flex-1 flex flex-col bg-gray-100">
            {/* PREVIEW HEADER */}
            <div className="bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Receipt Preview</h3>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex items-center gap-3">
                {/* DOWNLOAD */}
                <button
                  onClick={() =>
                    handleDownload(
                      selectedReceipt?.receipt_url,
                      `${receiptData?.orderNumber}-${selectedReceipt?._id}.pdf`,
                    )
                  }
                  className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all flex items-center gap-2 text-sm font-medium"
                >
                  <FiDownload />
                  Download
                </button>

                {/* PRINT */}
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all flex items-center gap-2 text-sm font-medium"
                >
                  <FiPrinter />
                  Print
                </button>
              </div>
            </div>

            {/* PDF PREVIEW */}
            <div className="flex-1 bg-[#EEF2F7] p-6 overflow-auto">
              {selectedReceipt?.receipt_url ? (
                <div className="w-full min-h-full flex justify-center">
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 w-full">
                    <iframe
                      src={`${selectedReceipt?.receipt_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
                      title="Receipt Preview"
                      className="w-full h-[800px] bg-white"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Preview Available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPaymentReceiptsDrawer;
